import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

type WregRadioOption<T extends string> = [T, string];

type Props<T extends string> = {
  value: T | null;
  onSelect: (value: T) => void;
  options: WregRadioOption<T>[];
  labelledBy: string;
  describedBy?: string;
  invalid?: boolean;
  id?: string;
};

const PREV_KEYS = ['ArrowUp', 'ArrowLeft'];
const NEXT_KEYS = ['ArrowDown', 'ArrowRight'];
const PICK_KEYS = [' ', 'Enter'];

const WregRadioGroup = <T extends string>({
  value,
  onSelect,
  options,
  labelledBy,
  describedBy,
  invalid,
  id,
}: Props<T>) => {
  const { t } = useTranslation();
  const refs = useRef<(HTMLLabelElement | null)[]>([]);
  const selectedIndex = options.findIndex(([answer]) => answer === value);
  const focusedIndex = selectedIndex < 0 ? 0 : selectedIndex;

  const step = (index: number, delta: number) => {
    const next = (index + delta + options.length) % options.length;
    onSelect(options[next][0]);
    refs.current[next]?.focus();
  };

  return (
    <div
      id={id}
      className={`c-radiogroup${invalid ? ' is-invalid' : ''}`}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      aria-required="true"
    >
      {options.map(([answer, key], index) => (
        <label
          key={answer}
          ref={(el) => {
            refs.current[index] = el;
          }}
          role="radio"
          aria-checked={value === answer}
          tabIndex={index === focusedIndex ? 0 : -1}
          className={`c-radio${value === answer ? ' on' : ''}`}
          onClick={() => onSelect(answer)}
          onKeyDown={(event) => {
            if (PICK_KEYS.includes(event.key)) {
              event.preventDefault();
              onSelect(answer);
              return;
            }
            if (NEXT_KEYS.includes(event.key)) {
              event.preventDefault();
              step(index, 1);
              return;
            }
            if (PREV_KEYS.includes(event.key)) {
              event.preventDefault();
              step(index, -1);
            }
          }}
        >
          <span className="ring" />
          {t(key)}
        </label>
      ))}
    </div>
  );
};

export { WregRadioGroup };
export type { WregRadioOption };
